import React, { useState } from "react";
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  UserGroupIcon,
  UserIcon
} from "@heroicons/react/24/outline";
import { Clock, BarChart3 } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ActivityLogs from '../components/ActivityLogs';
import Reports from '../components/Reports';

/* ------------------------------------------------------------------ */
/* MOCK DATA & CONSTANTS */
/* ------------------------------------------------------------------ */
const GLOBAL_PLANS = [
  { id: "health-1", name: "Swasthya Rakshak PPO", category: "Health", deduction: 12500 },
  { id: "dental-1", name: "Smiles & Sight Gold", category: "Dental/Vision", deduction: 2500 },
  { id: "well-1", name: "FitLife Membership", category: "Wellness", deduction: 500 },
];

const INITIAL_ENROLLMENTS = [
  { name: "Johnathan Doe", id: "EMP-2025-099", plans: 4, cost: 15500, frequency: "Monthly", dateAdded: "2024-01-15" },
  { name: "Arjun Mehta", id: "EMP-101", plans: 2, cost: 12500, frequency: "Annual", dateAdded: "2024-02-10" },
  { name: "Priya Rai", id: "EMP-104", plans: 3, cost: 13000, frequency: "One-time", dateAdded: "2024-03-05" },
];

const INITIAL_LOGS = [
  { id: 1, empId: "EMP-2025-099", action: "Benefit Added", modifiedBy: "HR001", date: "2024-01-15", details: "Health Insurance Plan" },
  { id: 2, empId: "EMP-101", action: "Benefit Modified", modifiedBy: "HR002", date: "2024-02-10", details: "Updated coverage amount" },
];

const HRBenefitsDashboard = () => {
  const [enrollments, setEnrollments] = useState(INITIAL_ENROLLMENTS);
  const [logs, setLogs] = useState(INITIAL_LOGS);
  const [empSearch, setEmpSearch] = useState("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [activeTab, setActiveTab] = useState("enrollment");
  
  // User Management specific activity types
  const userActivityTypes = [
    "User Created", "User Updated", "User Deactivated", 
    "Role Assigned", "Permission Changed", "Password Reset"
  ];

  // User Management specific report types
  const userReportTypes = [
    "User Activity Report", "Role Assignment Report", 
    "User Access Report", "Permission Audit Report",
    "User Registration Report", "Inactive Users Report"
  ];
  
  // State for Special Benefit Form
  const [specialBenefit, setSpecialBenefit] = useState({
    empIds: "",
    benefitName: "",
    amount: "",
    frequency: "One-time",
    dateAdded: new Date().toISOString().split('T')[0],
    assignmentType: "single"
  });

  // State for Assign Standard Plan Form
  const [assignForm, setAssignForm] = useState({
    empIds: "",
    empNames: "",
    planId: GLOBAL_PLANS[0].id,
    assignmentType: "single"
  });

  // State for Reports
  const [reportConfig, setReportConfig] = useState({
    type: "daily",
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  /* ------------------------------------------------------------------ */
  /* ACTIONS */
  /* ------------------------------------------------------------------ */
  
  const handleAddSpecialBenefit = () => {
    if (!specialBenefit.empIds || !specialBenefit.benefitName || !specialBenefit.amount) {
      alert("Please fill all fields");
      return;
    }

    const empIds = specialBenefit.assignmentType === "single" 
      ? [specialBenefit.empIds.trim()]
      : specialBenefit.empIds.split(',').map(id => id.trim()).filter(id => id);

    empIds.forEach(empId => {
      const existing = enrollments.find(e => e.id === empId);
      if (existing) {
        setEnrollments(prev => prev.map(e => 
          e.id === empId 
          ? { ...e, plans: e.plans + 1, cost: e.cost + parseInt(specialBenefit.amount), frequency: specialBenefit.frequency }
          : e
        ));
      } else {
        setEnrollments(prev => [...prev, {
          name: `User ${empId}`,
          id: empId,
          plans: 1,
          cost: parseInt(specialBenefit.amount),
          frequency: specialBenefit.frequency,
          dateAdded: specialBenefit.dateAdded
        }]);
      }
      
      // Add log entry
      setLogs(prev => [...prev, {
        id: Date.now() + Math.random(),
        empId,
        action: "Special Benefit Added",
        modifiedBy: "HR001",
        date: specialBenefit.dateAdded,
        details: `${specialBenefit.benefitName} - ₹${specialBenefit.amount} (${specialBenefit.frequency})`
      }]);
    });
    
    // Reset Form
    setSpecialBenefit({ 
      empIds: "", 
      benefitName: "", 
      amount: "", 
      frequency: "One-time", 
      dateAdded: new Date().toISOString().split('T')[0],
      assignmentType: "single"
    });
    alert(`Special Benefit Authorized for ${empIds.length} employee(s)!`);
  };

  const handleAssignStandard = () => {
    if (!assignForm.empIds || !assignForm.empNames) return;

    const selectedPlan = GLOBAL_PLANS.find(p => p.id === assignForm.planId);
    const empIds = assignForm.assignmentType === "single" 
      ? [assignForm.empIds.trim()]
      : assignForm.empIds.split(',').map(id => id.trim()).filter(id => id);
    const empNames = assignForm.assignmentType === "single" 
      ? [assignForm.empNames.trim()]
      : assignForm.empNames.split(',').map(name => name.trim()).filter(name => name);

    empIds.forEach((empId, index) => {
      const empName = empNames[index] || `User ${empId}`;
      const existing = enrollments.find(e => e.id === empId);

      if (existing) {
        setEnrollments(prev => prev.map(e => 
          e.id === empId 
          ? { ...e, plans: e.plans + 1, cost: e.cost + selectedPlan.deduction }
          : e
        ));
      } else {
        setEnrollments(prev => [...prev, {
          name: empName,
          id: empId,
          plans: 1,
          cost: selectedPlan.deduction,
          frequency: "Monthly",
          dateAdded: new Date().toISOString().split('T')[0]
        }]);
      }
      
      setLogs(prev => [...prev, {
        id: Date.now() + Math.random(),
        empId,
        action: "Standard Plan Assigned",
        modifiedBy: "HR001",
        date: new Date().toISOString().split('T')[0],
        details: `${selectedPlan.name} - ₹${selectedPlan.deduction}`
      }]);
    });
    
    setShowAssignModal(false);
    setAssignForm({ empIds: "", empNames: "", planId: GLOBAL_PLANS[0].id, assignmentType: "single" });
  };

  const handleDelete = (id) => {
    if(window.confirm("Are you sure you want to remove all benefits for this employee?")) {
      setEnrollments(enrollments.filter(e => e.id !== id));
    }
  };

  const handleEdit = (id) => {
    const name = prompt("Enter new Display Name:");
    if (name) {
      setEnrollments(enrollments.map(e => e.id === id ? { ...e, name } : e));
      setLogs(prev => [...prev, {
        id: Date.now(),
        empId: id,
        action: "Employee Name Updated",
        modifiedBy: "HR001",
        date: new Date().toISOString().split('T')[0],
        details: `Name changed to: ${name}`
      }]);
    }
  };

  const downloadExcel = (data, filename, headers, title) => {
    const reportData = [
      [title],
      [`Generated on: ${new Date().toLocaleDateString()}`],
      [`Report Type: ${reportConfig.type.charAt(0).toUpperCase() + reportConfig.type.slice(1)}`],
      [],
      headers,
      ...data.map(item => headers.map(header => {
        const key = header.toLowerCase().replace(/\s+/g, '');
        return item[key] || item[header] || '';
      }))
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const downloadPDF = (data, filename, headers, title) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(title, 20, 20);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
    doc.text(`Report Type: ${reportConfig.type.charAt(0).toUpperCase() + reportConfig.type.slice(1)}`, 20, 40);
    
    const tableData = data.map(item => headers.map(header => {
      const key = header.toLowerCase().replace(/\s+/g, '');
      return item[key] || item[header] || '';
    }));
    
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 50,
      styles: { fontSize: 8 }
    });
    
    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const generateReport = (type) => {
    let data = [];
    let filename = "";
    let headers = [];
    let title = "";

    if (activeTab === "enrollment") {
      data = enrollments.map(e => ({
        employee: e.name,
        employeeid: e.id,
        activeplans: e.plans,
        monthlycost: `₹${e.cost}`,
        frequency: e.frequency,
        dateadded: e.dateAdded
      }));
      headers = ["Employee", "Employee ID", "Active Plans", "Monthly Cost", "Frequency", "Date Added"];
      filename = `enrollment_report_${type}`;
      title = "Employee Benefit Enrollment Report";
    } else if (activeTab === "logs") {
      data = logs.map(l => ({
        employeeid: l.empId,
        action: l.action,
        modifiedby: l.modifiedBy,
        date: l.date,
        details: l.details
      }));
      headers = ["Employee ID", "Action", "Modified By", "Date", "Details"];
      filename = `logs_report_${type}`;
      title = "Activity Logs Report";
    }

    return { data, filename, headers, title };
  };

  const handleDownload = (format) => {
    const report = generateReport(reportConfig.type);
    if (format === "excel") {
      downloadExcel(report.data, report.filename, report.headers, report.title);
    } else {
      downloadPDF(report.data, report.filename, report.headers, report.title);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-roboto text-slate-800">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Roboto:wght@400;500;700&display=swap');
        .font-poppins { font-family: 'Poppins', sans-serif; }
        .font-roboto { font-family: 'Roboto', sans-serif; }
      `}</style>

      {/* MODAL FOR ASSIGN STANDARD PLAN */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-poppins font-bold text-sm">Assign Standard Plan</h3>
              <button onClick={() => setShowAssignModal(false)}><XMarkIcon className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Assignment Type</label>
                <div className="flex gap-2">
                  <button 
                    type="button"
                    onClick={() => setAssignForm({...assignForm, assignmentType: "single"})}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded border ${
                      assignForm.assignmentType === "single" 
                        ? "bg-blue-50 border-blue-200 text-blue-700" 
                        : "border-slate-200 text-slate-600"
                    }`}
                  >
                    <UserIcon className="h-3 w-3" /> Single
                  </button>
                  <button 
                    type="button"
                    onClick={() => setAssignForm({...assignForm, assignmentType: "multiple"})}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded border ${
                      assignForm.assignmentType === "multiple" 
                        ? "bg-blue-50 border-blue-200 text-blue-700" 
                        : "border-slate-200 text-slate-600"
                    }`}
                  >
                    <UserGroupIcon className="h-3 w-3" /> Multiple
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  {assignForm.assignmentType === "single" ? "Employee Name" : "Employee Names (comma separated)"}
                </label>
                <input type="text" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm outline-none focus:border-blue-500" 
                  placeholder={assignForm.assignmentType === "single" ? "John Doe" : "John Doe, Jane Smith, Mike Johnson"}
                  value={assignForm.empNames} onChange={e => setAssignForm({...assignForm, empNames: e.target.value})} />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  {assignForm.assignmentType === "single" ? "Employee ID" : "Employee IDs (comma separated)"}
                </label>
                <input type="text" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm outline-none focus:border-blue-500" 
                  placeholder={assignForm.assignmentType === "single" ? "EMP-2025-099" : "EMP-101, EMP-102, EMP-103"}
                  value={assignForm.empIds} onChange={e => setAssignForm({...assignForm, empIds: e.target.value})} />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Select Master Plan</label>
                <select className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm outline-none bg-white"
                  value={assignForm.planId} onChange={e => setAssignForm({...assignForm, planId: e.target.value})}>
                  {GLOBAL_PLANS.map(p => <option key={p.id} value={p.id}>{p.name} (₹{p.deduction})</option>)}
                </select>
              </div>
              <button onClick={handleAssignStandard} className="w-full bg-blue-600 text-white font-bold text-xs py-2.5 rounded uppercase flex items-center justify-center gap-2">
                <CheckIcon className="h-4 w-4" /> Confirm Enrollment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-lg font-poppins font-bold text-slate-900">Benefits Administration</h1>
            <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wider">HR Control Panel • 2026 Fiscal Year</p>
          </div>
          <button 
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-bold transition"
          >
            <PlusIcon className="h-4 w-4" /> ASSIGN STANDARD PLAN
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          
          {/* LEFT: SPECIAL BENEFITS ASSIGNMENT */}
          <section className="lg:col-span-1 bg-white border border-slate-200 rounded-lg p-4 shadow-sm h-fit">
            <h3 className="text-xs font-poppins font-bold text-slate-900 uppercase mb-4 flex items-center gap-2">
              <PlusIcon className="h-4 w-4 text-blue-600" /> Add Special Benefit
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Assignment Type</label>
                <div className="flex gap-2">
                  <button 
                    type="button"
                    onClick={() => setSpecialBenefit({...specialBenefit, assignmentType: "single"})}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded border ${
                      specialBenefit.assignmentType === "single" 
                        ? "bg-blue-50 border-blue-200 text-blue-700" 
                        : "border-slate-200 text-slate-600"
                    }`}
                  >
                    <UserIcon className="h-3 w-3" /> Single
                  </button>
                  <button 
                    type="button"
                    onClick={() => setSpecialBenefit({...specialBenefit, assignmentType: "multiple"})}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded border ${
                      specialBenefit.assignmentType === "multiple" 
                        ? "bg-blue-50 border-blue-200 text-blue-700" 
                        : "border-slate-200 text-slate-600"
                    }`}
                  >
                    <UserGroupIcon className="h-3 w-3" /> Multiple
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  {specialBenefit.assignmentType === "single" ? "Employee ID" : "Employee IDs (comma separated)"}
                </label>
                <input 
                  type="text" 
                  placeholder={specialBenefit.assignmentType === "single" ? "e.g. EMP-2025-099" : "e.g. EMP-101, EMP-102, EMP-103"}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                  value={specialBenefit.empIds}
                  onChange={(e) => setSpecialBenefit({...specialBenefit, empIds: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Benefit Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. Remote Office Stipend" 
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                  value={specialBenefit.benefitName}
                  onChange={(e) => setSpecialBenefit({...specialBenefit, benefitName: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Date of Adding</label>
                <input 
                  type="date" 
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                  value={specialBenefit.dateAdded}
                  onChange={(e) => setSpecialBenefit({...specialBenefit, dateAdded: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Amount (₹)</label>
                  <input 
                    type="number" 
                    placeholder="5000" 
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                    value={specialBenefit.amount}
                    onChange={(e) => setSpecialBenefit({...specialBenefit, amount: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Frequency</label>
                  <select 
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm bg-white outline-none"
                    value={specialBenefit.frequency}
                    onChange={(e) => setSpecialBenefit({...specialBenefit, frequency: e.target.value})}
                  >
                    <option>One-time</option>
                    <option>Monthly</option>
                    <option>Quarterly</option>
                    <option>Half-yearly</option>
                    <option>Annual</option>
                  </select>
                </div>
              </div>
              <button 
                onClick={handleAddSpecialBenefit}
                className="w-full bg-slate-900 text-white text-[10px] font-bold py-2 rounded uppercase hover:bg-slate-800 transition mt-2"
              >
                Authorize & Add Benefit
              </button>
            </div>
          </section>

          {/* RIGHT: MAIN CONTENT AREA */}
          <section className="lg:col-span-2 space-y-4">
            {/* Tab Navigation */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
              <div className="border-b border-slate-200">
                <nav className="flex space-x-8 px-4">
                  <button
                    onClick={() => setActiveTab("enrollment")}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "enrollment"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    Employee Benefit Enrollment
                  </button>
                  <button
                    onClick={() => setActiveTab("reports")}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "reports"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    Reports
                  </button>
                  <button
                    onClick={() => setActiveTab("logs")}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "logs"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    Activity Logs
                  </button>
                </nav>
              </div>

              {/* Enrollment Tab */}
              {activeTab === "enrollment" && (
                <>
                  <div className="p-3 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="text-xs font-poppins font-bold text-slate-900 uppercase">Employee Benefit Enrollment</h3>
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-3.5 w-3.5 absolute left-2 top-2 text-slate-400" />
                      <input 
                        type="text" 
                        placeholder="Search Emp ID..." 
                        className="pl-7 pr-2 py-1 border border-slate-200 rounded text-xs focus:ring-1 focus:ring-blue-500 outline-none w-48"
                        onChange={(e) => setEmpSearch(e.target.value)}
                      />
                    </div>
                  </div>
              
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Employee</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Active Plans</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Monthly Cost</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Frequency</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Date Added</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {enrollments.filter(e => e.id.toLowerCase().includes(empSearch.toLowerCase())).map((row) => (
                          <tr key={row.id} className="hover:bg-slate-50 transition">
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-slate-800">{row.name}</p>
                              <p className="text-[10px] font-bold text-blue-600 uppercase">{row.id}</p>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-600">
                              <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-bold mr-1">{row.plans} Active</span>
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-slate-700">₹{row.cost.toLocaleString()}</td>
                            <td className="px-4 py-3 text-xs text-slate-600">
                              <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-bold">{row.frequency}</span>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-600">{row.dateAdded}</td>
                            <td className="px-4 py-3 text-right space-x-3">
                              <button 
                                onClick={() => handleEdit(row.id)}
                                className="text-[10px] font-bold text-slate-400 uppercase hover:text-blue-600 underline"
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => handleDelete(row.id)}
                                className="text-[10px] font-bold text-slate-400 uppercase hover:text-red-600"
                              >
                                <TrashIcon className="h-4 w-4 inline mb-0.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Reports Tab */}
              {activeTab === "reports" && (
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Report Type</label>
                      <select 
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm bg-white outline-none"
                        value={reportConfig.type}
                        onChange={(e) => setReportConfig({...reportConfig, type: e.target.value})}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="half-yearly">Half Yearly</option>
                        <option value="yearly">Yearly</option>
                        <option value="custom">Custom Date Range</option>
                      </select>
                    </div>
                    {reportConfig.type === "custom" && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Start Date</label>
                          <input 
                            type="date" 
                            className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm outline-none"
                            value={reportConfig.startDate}
                            onChange={(e) => setReportConfig({...reportConfig, startDate: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">End Date</label>
                          <input 
                            type="date" 
                            className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm outline-none"
                            value={reportConfig.endDate}
                            onChange={(e) => setReportConfig({...reportConfig, endDate: e.target.value})}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleDownload("excel")}
                      className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-green-700 transition"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4" /> Download Excel
                    </button>
                    <button 
                      onClick={() => handleDownload("pdf")}
                      className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-red-700 transition"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4" /> Download PDF
                    </button>
                  </div>
                </div>
              )}

              {/* Logs Tab */}
              {activeTab === "logs" && (
                <>
                  <div className="p-3 border-b border-slate-100">
                    <h3 className="text-xs font-poppins font-bold text-slate-900 uppercase flex items-center gap-2">
                      <ClockIcon className="h-4 w-4" /> Activity Logs
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Employee ID</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Action</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Modified By</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {logs.map((log) => (
                          <tr key={log.id} className="hover:bg-slate-50 transition">
                            <td className="px-4 py-3 text-sm font-bold text-blue-600">{log.empId}</td>
                            <td className="px-4 py-3 text-xs text-slate-600">
                              <span className="bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded font-bold">{log.action}</span>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-600">{log.modifiedBy}</td>
                            <td className="px-4 py-3 text-xs text-slate-600">{log.date}</td>
                            <td className="px-4 py-3 text-xs text-slate-600">{log.details}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="p-4 border-t border-slate-100">
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleDownload("excel")}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-green-700 transition"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" /> Download Excel
                      </button>
                      <button 
                        onClick={() => handleDownload("pdf")}
                        className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-red-700 transition"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" /> Download PDF
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
        </div>

        {/* BOTTOM: MASTER PLAN TEMPLATES */}
        <section className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
           <h3 className="text-xs font-poppins font-bold text-slate-900 uppercase mb-4">Master Benefit Templates</h3>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {GLOBAL_PLANS.map(plan => (
                <div key={plan.id} className="border border-slate-100 rounded-md p-3 bg-slate-50/50">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-bold uppercase">{plan.category}</span>
                    <button className="text-slate-400 hover:text-blue-600"><AdjustmentsHorizontalIcon className="h-4 w-4" /></button>
                  </div>
                  <h4 className="text-sm font-bold text-slate-800">{plan.name}</h4>
                  <p className="text-xs text-slate-500 mt-1">Standard Deduction: ₹{plan.deduction.toLocaleString()} / mo</p>
                </div>
              ))}
           </div>
        </section>
      </main>
    </div>
  );
};

export default HRBenefitsDashboard;